import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface SipTrunkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partnerName: string
  currentTrunkId: string | null
  onSave: (sipTrunkId: string | null) => Promise<void>
}

export function SipTrunkDialog({
  open,
  onOpenChange,
  partnerName,
  currentTrunkId,
  onSave,
}: SipTrunkDialogProps) {
  const [value, setValue] = useState(currentTrunkId ?? '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setValue(currentTrunkId ?? '')
  }, [currentTrunkId, open])

  const handleSave = async () => {
    setSaving(true)
    try {
      const trimmed = value.trim()
      await onSave(trimmed === '' ? null : trimmed)
      onOpenChange(false)
    } catch {
      // Caller already surfaced the error to the user; keep the dialog open so
      // they can retry or correct the input.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>SIP trunk for {partnerName}</DialogTitle>
          <DialogDescription>
            LiveKit outbound SIP trunk ID. When set, outbound calls dispatched
            for companies provisioned by this partner route through this trunk
            instead of the global env default. Leave blank to clear.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="sip-trunk-id">Trunk ID</Label>
          <Input
            id="sip-trunk-id"
            placeholder="ST_..."
            value={value}
            onChange={e => setValue(e.target.value)}
            autoComplete="off"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
